/**
 * 財務區共用日期區間 picker
 *
 * - 沒有 preset chip。永遠是自訂模式。
 * - mode="month"：使用自製 popover month picker（年份切換 + 12 個月份格子）
 * - mode="range"：兩個原生 <input type="date">，永遠可編輯
 * - state 來自 useFinancialDateRange（sessionStorage 跨 tab 記憶）
 */
import { useState } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  useFinancialDateRange,
  periodFromRange,
} from "@/store/financialDateRange";

const MONTH_LABELS = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];

interface MonthPickerProps {
  value: string; // YYYY-MM
  onChange: (period: string) => void;
}

function MonthPicker({ value, onChange }: MonthPickerProps) {
  const [y0, m0] = value.split("-").map(Number);
  const now = new Date();
  const [navYear, setNavYear] = useState(y0);
  const [open, setOpen] = useState(false);

  const handlePick = (month: number) => {
    const period = `${navYear}-${String(month).padStart(2, "0")}`;
    onChange(period);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={(next) => {
      // 開啟時把 navYear 重設到目前選的年
      if (next) setNavYear(y0);
      setOpen(next);
    }}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="h-9 min-w-[8rem] justify-start gap-2 font-normal">
          <Calendar className="h-4 w-4" />
          {y0} 年 {m0} 月
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="p-3 w-64">
        {/* 年份切換 */}
        <div className="flex items-center justify-between mb-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.preventDefault(); setNavYear((y) => y - 1); }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-medium">{navYear} 年</div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.preventDefault(); setNavYear((y) => y + 1); }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {/* 12 月份格子（4×3） */}
        <div className="grid grid-cols-4 gap-1">
          {MONTH_LABELS.map((label, i) => {
            const month = i + 1;
            const selected = navYear === y0 && month === m0;
            const isThisMonth =
              navYear === now.getFullYear() && month === now.getMonth() + 1;
            return (
              <button
                key={month}
                type="button"
                onClick={() => handlePick(month)}
                className={cn(
                  "rounded-md px-2 py-2 text-sm transition-colors",
                  selected
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : isThisMonth
                    ? "bg-accent text-accent-foreground hover:bg-accent/80"
                    : "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface FinancialDateRangePickerProps {
  /** "range"：顯示 start + end；"month"：只顯示 month picker。預設 "range" */
  mode?: "range" | "month";
  className?: string;
}

export default function FinancialDateRangePicker({
  mode = "range",
  className,
}: FinancialDateRangePickerProps) {
  const { start, end, setRange, setMonth } = useFinancialDateRange();
  const period = periodFromRange(start);

  if (mode === "month") {
    return (
      <div className={cn("flex items-end gap-2", className)}>
        <MonthPicker value={period} onChange={setMonth} />
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-end gap-2", className)}>
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          整月快選
        </label>
        <MonthPicker value={period} onChange={setMonth} />
      </div>
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          開始日期
        </label>
        <Input
          type="date"
          value={start}
          onChange={(e) => setRange(e.target.value, end)}
          className="w-40"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">
          結束日期
        </label>
        <Input
          type="date"
          value={end}
          onChange={(e) => setRange(start, e.target.value)}
          className="w-40"
        />
      </div>
    </div>
  );
}
